import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  LinearProgress,
  Tooltip,
  Card,
  CardContent,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  CloudUpload,
  Download,
  Description,
  Assignment,
  CheckCircle,
  TableChart,
  Close
} from '@mui/icons-material';
import { useActivityLog } from '../../context/ActivityLogContext';
import { API_ENDPOINTS, apiCall } from '../../config/api';
import FavoriteButton from '../../components/FavoriteButton';
import { TOOL_COLORS, getCardBackground } from '../../constants/colors';

const HomeGoods = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const {
    addActivityProcess,
    updateActivityProcess,
    setLogs: setActivityLogs,
    completeActivityProcess,
    setIntervalRef,
    clearIntervalRef,
  } = useActivityLog();

  const [orderFile, setOrderFile] = useState(null);
  const [distributionFile, setDistributionFile] = useState(null);
  const [orderFilePath, setOrderFilePath] = useState('');
  const [distributionFilePath, setDistributionFilePath] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [results, setResults] = useState(null);
  const [isDraggingOrder, setIsDraggingOrder] = useState(false);
  const [isDraggingDistribution, setIsDraggingDistribution] = useState(false);
  const [processId, setProcessId] = useState(null);

  const intervalRef = useRef(null);
  const processIdRef = useRef(null);
  const orderInputRef = useRef(null);
  const distributionInputRef = useRef(null);

  // Colors that work in both light and dark mode
  const primaryColor = TOOL_COLORS.PDF.primary;
  const successColor = '#4CAF50';

  const handleOrderFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/pdf') {
      setOrderFile(file);
      setOrderFilePath(file.name);
      setError('');
    } else if (file) {
      setError('Please select a valid PDF file for the order document.');
    }
  };

  const handleDistributionFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/pdf') {
      setDistributionFile(file);
      setDistributionFilePath(file.name);
      setError('');
    } else if (file) {
      setError('Please select a valid PDF file for the distribution document.');
    }
  };

  // Fetch status for processing
  const fetchStatus = async () => {
    const currentProcessId = processIdRef.current;
    if (!currentProcessId) return;

    try {
      const statusData = await apiCall(API_ENDPOINTS.HOMEGOODS_STATUS(currentProcessId), {
        method: 'GET',
      });

      if (statusData) {
        const progressPct = statusData.progressPercentage ?? 0;
        const totalItems = statusData.totalItems ?? 0;
        const processedItems = statusData.processedItems ?? 0;
        const logs = statusData.logs ?? [];
        const currentStatus = statusData.status ?? '';

        // Update global activity log
        updateActivityProcess(currentProcessId, {
          progressPercentage: progressPct,
          totalItems: totalItems,
          processedItems: processedItems,
        });

        // Update logs in global context
        if (logs && logs.length > 0) {
          setActivityLogs(currentProcessId, logs);
        }

        // Check if processing is complete
        if (currentStatus === 'completed' || currentStatus === 'error') {
          // Clear interval
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          clearIntervalRef(currentProcessId);

          // Complete the activity process
          completeActivityProcess(currentProcessId, currentStatus === 'completed');

          if (currentStatus === 'completed') {
            setSuccess('Files processed successfully — downloading...');
            const completedResults = {
              success: true,
              summaryFile: statusData.summaryFileName,
              importFile: statusData.importFileName,
              orderNumber: statusData.orderNumber,
              department: statusData.department,
              processId: currentProcessId
            };
            setResults(completedResults);
            // Auto-download both files (matches PO Distribution / Marketing Copies UX)
            autoDownloadResults(completedResults);
          } else {
            setError('Processing failed. Check the activity log for details.');
          }

          setProcessing(false);
        }
      }
    } catch (err) {
      console.error('Error fetching status:', err);
    }
  };

  const handleProcessFiles = async () => {
    if (!orderFile || !distributionFile) {
      setError('Please select both order and distribution files before processing.');
      return;
    }

    setProcessing(true);
    setError('');
    setSuccess('');
    setResults(null);

    try {
      const formData = new FormData();
      formData.append('orderFile', orderFile);
      formData.append('distributionFile', distributionFile);

      const result = await apiCall(API_ENDPOINTS.HOMEGOODS_PROCESS, {
        method: 'POST',
        body: formData,
      });

      if (result.processId) {
        setProcessId(result.processId);
        processIdRef.current = result.processId;

        // Add to global activity log
        addActivityProcess(result.processId, `Home Goods: ${orderFile.name}`, 'Home Goods PDF');

        // Start polling for status
        intervalRef.current = setInterval(() => {
          fetchStatus();
        }, 1000);
        setIntervalRef(result.processId, intervalRef.current);
      } else {
        setError('Failed to start processing');
        setProcessing(false);
      }
    } catch (err) {
      setError(`Processing failed: ${err.message}`);
      setProcessing(false);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Download both result files automatically once processing completes.
  // Triggers two browser downloads back-to-back (small delay between them
  // so the second isn't suppressed as a popup).
  const autoDownloadResults = async (resultData) => {
    if (!resultData?.processId) return;
    try {
      const summaryResp = await apiCall(API_ENDPOINTS.HOMEGOODS_DOWNLOAD_SUMMARY(resultData.processId));
      const summaryBlob = await summaryResp.blob();
      const summaryUrl = window.URL.createObjectURL(summaryBlob);
      const summaryLink = document.createElement('a');
      summaryLink.href = summaryUrl;
      summaryLink.download = resultData.summaryFile || 'HG_PO_DC_Summary.xlsx';
      summaryLink.click();
      window.URL.revokeObjectURL(summaryUrl);
    } catch (err) {
      setError(`Summary download failed: ${err.message}`);
      return;
    }

    // Brief gap so the browser allows the second download
    await new Promise(r => setTimeout(r, 400));

    try {
      const importResp = await apiCall(API_ENDPOINTS.HOMEGOODS_DOWNLOAD_IMPORT(resultData.processId));
      const importBlob = await importResp.blob();
      const importUrl = window.URL.createObjectURL(importBlob);
      const importLink = document.createElement('a');
      importLink.href = importUrl;
      importLink.download = resultData.importFile || 'HG_PO_Import.xlsx';
      importLink.click();
      window.URL.revokeObjectURL(importUrl);
    } catch (err) {
      setError(`Import download failed: ${err.message}`);
    }
  };

  const handleDownloadSummary = async () => {
    if (!results?.processId) return;

    try {
      const response = await apiCall(API_ENDPOINTS.HOMEGOODS_DOWNLOAD_SUMMARY(results.processId));
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = results.summaryFile || 'HG_PO_DC_Summary.xlsx';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(`Download failed: ${err.message}`);
    }
  };

  const handleDownloadImport = async () => {
    if (!results?.processId) return;

    try {
      const response = await apiCall(API_ENDPOINTS.HOMEGOODS_DOWNLOAD_IMPORT(results.processId));
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = results.importFile || 'HG_PO_Import.xlsx';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(`Download failed: ${err.message}`);
    }
  };

  const resetForm = () => {
    setOrderFile(null);
    setDistributionFile(null);
    setOrderFilePath('');
    setDistributionFilePath('');
    setError('');
    setSuccess('');
    setResults(null);
  };

  const clearOrderFile = (e) => {
    e.stopPropagation();
    setOrderFile(null);
    setOrderFilePath('');
    if (orderInputRef.current) {
      orderInputRef.current.value = '';
    }
  };

  const clearDistributionFile = (e) => {
    e.stopPropagation();
    setDistributionFile(null);
    setDistributionFilePath('');
    if (distributionInputRef.current) {
      distributionInputRef.current.value = '';
    }
  };

  // Drag and drop handlers for Order File
  const handleOrderDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOrder(true);
  };

  const handleOrderDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOrder(false);
  };

  const handleOrderDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleOrderDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOrder(false);

    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles && droppedFiles.length > 0) {
      const file = droppedFiles[0];
      if (file && file.type === 'application/pdf') {
        setOrderFile(file);
        setOrderFilePath(file.name);
        setError('');
      } else {
        setError('Please select a valid PDF file for the order document.');
      }
    }
  };

  // Drag and drop handlers for Distribution File
  const handleDistributionDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingDistribution(true);
  };

  const handleDistributionDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingDistribution(false);
  };

  const handleDistributionDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDistributionDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingDistribution(false);

    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles && droppedFiles.length > 0) {
      const file = droppedFiles[0];
      if (file && file.type === 'application/pdf') {
        setDistributionFile(file);
        setDistributionFilePath(file.name);
        setError('');
      } else {
        setError('Please select a valid PDF file for the distribution document.');
      }
    }
  };

  // Click handler for drop zones
  const handleOrderZoneClick = () => {
    orderInputRef.current?.click();
  };

  const handleDistributionZoneClick = () => {
    distributionInputRef.current?.click();
  };

  // Get border color based on state
  const getDropZoneBorderColor = (isDragging, hasFile) => {
    if (isDragging) return primaryColor;
    if (hasFile) return successColor;
    return theme.palette.divider;
  };

  // Get background color based on state (dark mode compatible)
  const getDropZoneBgColor = (isDragging, hasFile) => {
    if (isDragging) return alpha(primaryColor, 0.1);
    if (hasFile) return alpha(successColor, isDarkMode ? 0.15 : 0.08);
    return 'transparent';
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto', position: 'relative' }}>
        <FavoriteButton interfaceName="Home Goods PDF" category="pdf" />
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            color: theme.palette.text.primary,
            textAlign: 'center',
            fontWeight: 600,
            letterSpacing: '0.5px',
            borderBottom: `2px solid ${theme.palette.divider}`,
            pb: 2,
            mb: 4
          }}
        >
          HOME GOODS PDF
        </Typography>

        <Grid container spacing={3}>
          {/* File Upload Section */}
          <Grid item xs={12} md={8}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: theme.palette.background.paper
              }}
            >
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: theme.palette.text.primary }}>
                File Import
              </Typography>
              <Typography variant="body2" sx={{ mb: 3, color: theme.palette.text.secondary }}>
                Upload both Order and Distribution PDF files to generate summary and import Excel files
              </Typography>

              {/* Order File Upload */}
              <Card
                sx={{
                  mb: 3,
                  bgcolor: orderFile ? alpha(successColor, isDarkMode ? 0.1 : 0.05) : theme.palette.background.paper,
                  border: `1px solid ${orderFile ? alpha(successColor, 0.3) : theme.palette.divider}`
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Description sx={{ color: primaryColor }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                      Order File (PDF)
                    </Typography>
                    {orderFile && (
                      <Tooltip title="File selected">
                        <CheckCircle sx={{ color: successColor }} />
                      </Tooltip>
                    )}
                  </Box>

                  {/* Hidden file input */}
                  <input
                    ref={orderInputRef}
                    type="file"
                    hidden
                    accept=".pdf"
                    onChange={handleOrderFileUpload}
                  />

                  {/* Clickable Drag and Drop Zone */}
                  <Box
                    onClick={handleOrderZoneClick}
                    onDragEnter={handleOrderDragEnter}
                    onDragOver={handleOrderDragOver}
                    onDragLeave={handleOrderDragLeave}
                    onDrop={handleOrderDrop}
                    sx={{
                      border: `2px dashed ${getDropZoneBorderColor(isDraggingOrder, orderFile)}`,
                      borderRadius: 2,
                      p: 3,
                      textAlign: 'center',
                      bgcolor: getDropZoneBgColor(isDraggingOrder, orderFile),
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      position: 'relative',
                      '&:hover': {
                        borderColor: orderFile ? successColor : primaryColor,
                        bgcolor: orderFile ? alpha(successColor, isDarkMode ? 0.2 : 0.1) : alpha(primaryColor, 0.05),
                      },
                    }}
                  >
                    {orderFile && (
                      <Tooltip title="Remove file">
                        <Box
                          onClick={clearOrderFile}
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            bgcolor: alpha(theme.palette.error.main, 0.1),
                            borderRadius: '50%',
                            p: 0.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            '&:hover': {
                              bgcolor: alpha(theme.palette.error.main, 0.2),
                            }
                          }}
                        >
                          <Close sx={{ fontSize: 18, color: theme.palette.error.main }} />
                        </Box>
                      </Tooltip>
                    )}
                    {orderFile ? (
                      <>
                        <CheckCircle sx={{ fontSize: 48, color: successColor, mb: 1 }} />
                        <Typography variant="body1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.text.primary }}>
                          1 file selected
                        </Typography>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                          Click to replace
                        </Typography>
                      </>
                    ) : (
                      <>
                        <CloudUpload sx={{ fontSize: 48, color: isDraggingOrder ? primaryColor : theme.palette.text.secondary, mb: 1 }} />
                        <Typography variant="body1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.text.primary }}>
                          {isDraggingOrder ? 'Drop file here' : 'Drag & drop or click to select'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                          PDF files only
                        </Typography>
                      </>
                    )}
                  </Box>
                  {orderFile && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mt: 1.5,
                        p: 1,
                        bgcolor: 'background.paper',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                        <Description sx={{ color: primaryColor, fontSize: 20 }} />
                        <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {orderFile.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                          ({(orderFile.size / 1024 / 1024).toFixed(2)} MB)
                        </Typography>
                      </Box>
                      <Box
                        onClick={clearOrderFile}
                        sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', p: 0.5, borderRadius: '50%', '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.1) } }}
                      >
                        <Close sx={{ fontSize: 18, color: theme.palette.error.main }} />
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>

              {/* Distribution File Upload */}
              <Card
                sx={{
                  mb: 3,
                  bgcolor: distributionFile ? alpha(successColor, isDarkMode ? 0.1 : 0.05) : theme.palette.background.paper,
                  border: `1px solid ${distributionFile ? alpha(successColor, 0.3) : theme.palette.divider}`
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Assignment sx={{ color: primaryColor }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                      Distribution/DC File (PDF)
                    </Typography>
                    {distributionFile && (
                      <Tooltip title="File selected">
                        <CheckCircle sx={{ color: successColor }} />
                      </Tooltip>
                    )}
                  </Box>

                  {/* Hidden file input */}
                  <input
                    ref={distributionInputRef}
                    type="file"
                    hidden
                    accept=".pdf"
                    onChange={handleDistributionFileUpload}
                  />

                  {/* Clickable Drag and Drop Zone */}
                  <Box
                    onClick={handleDistributionZoneClick}
                    onDragEnter={handleDistributionDragEnter}
                    onDragOver={handleDistributionDragOver}
                    onDragLeave={handleDistributionDragLeave}
                    onDrop={handleDistributionDrop}
                    sx={{
                      border: `2px dashed ${getDropZoneBorderColor(isDraggingDistribution, distributionFile)}`,
                      borderRadius: 2,
                      p: 3,
                      textAlign: 'center',
                      bgcolor: getDropZoneBgColor(isDraggingDistribution, distributionFile),
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      position: 'relative',
                      '&:hover': {
                        borderColor: distributionFile ? successColor : primaryColor,
                        bgcolor: distributionFile ? alpha(successColor, isDarkMode ? 0.2 : 0.1) : alpha(primaryColor, 0.05),
                      },
                    }}
                  >
                    {distributionFile && (
                      <Tooltip title="Remove file">
                        <Box
                          onClick={clearDistributionFile}
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            bgcolor: alpha(theme.palette.error.main, 0.1),
                            borderRadius: '50%',
                            p: 0.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            '&:hover': {
                              bgcolor: alpha(theme.palette.error.main, 0.2),
                            }
                          }}
                        >
                          <Close sx={{ fontSize: 18, color: theme.palette.error.main }} />
                        </Box>
                      </Tooltip>
                    )}
                    {distributionFile ? (
                      <>
                        <CheckCircle sx={{ fontSize: 48, color: successColor, mb: 1 }} />
                        <Typography variant="body1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.text.primary }}>
                          1 file selected
                        </Typography>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                          Click to replace
                        </Typography>
                      </>
                    ) : (
                      <>
                        <CloudUpload sx={{ fontSize: 48, color: isDraggingDistribution ? primaryColor : theme.palette.text.secondary, mb: 1 }} />
                        <Typography variant="body1" sx={{ fontWeight: 600, mb: 1, color: theme.palette.text.primary }}>
                          {isDraggingDistribution ? 'Drop file here' : 'Drag & drop or click to select'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                          PDF files only
                        </Typography>
                      </>
                    )}
                  </Box>
                  {distributionFile && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mt: 1.5,
                        p: 1,
                        bgcolor: 'background.paper',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                        <Description sx={{ color: primaryColor, fontSize: 20 }} />
                        <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {distributionFile.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
                          ({(distributionFile.size / 1024 / 1024).toFixed(2)} MB)
                        </Typography>
                      </Box>
                      <Box
                        onClick={clearDistributionFile}
                        sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', p: 0.5, borderRadius: '50%', '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.1) } }}
                      >
                        <Close sx={{ fontSize: 18, color: theme.palette.error.main }} />
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>

              {/* Process Button */}
              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={handleProcessFiles}
                  disabled={!orderFile || !distributionFile || processing}
                  sx={{
                    bgcolor: primaryColor,
                    '&:hover': { bgcolor: TOOL_COLORS.PDF.hover },
                    '&:disabled': {
                      bgcolor: alpha(primaryColor, 0.3),
                      color: theme.palette.text.disabled
                    },
                    px: 4
                  }}
                >
                  {processing ? 'Processing...' : 'Process Files'}
                </Button>

                {(orderFile || distributionFile || results) && (
                  <Button
                    variant="outlined"
                    onClick={resetForm}
                    disabled={processing}
                    sx={{
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                      '&:hover': {
                        borderColor: theme.palette.text.secondary,
                        bgcolor: alpha(theme.palette.text.primary, 0.05)
                      }
                    }}
                  >
                    Clear
                  </Button>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Status & Download Section */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: theme.palette.background.paper
              }}
            >
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: theme.palette.text.primary }}>
                Status & Downloads
              </Typography>

              {/* Progress */}
              {processing && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1, color: theme.palette.text.secondary }}>
                    Processing files...
                  </Typography>
                  <LinearProgress
                    sx={{
                      bgcolor: alpha(primaryColor, 0.2),
                      '& .MuiLinearProgress-bar': { bgcolor: primaryColor }
                    }}
                  />
                </Box>
              )}

              {/* Error Alert */}
              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              {/* Success Alert */}
              {success && (
                <Alert severity="success" sx={{ mb: 3 }}>
                  {success}
                </Alert>
              )}

              {/* Download Section: files auto-download when processing
                  completes; this just shows confirmation. */}
              {results && (
                <Box sx={{ mb: 2 }}>
                  <Alert severity="success" icon={<CheckCircle />}>
                    Files downloaded: <strong>{results.summaryFile}</strong> · <strong>{results.importFile}</strong>
                  </Alert>
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Expected Output */}
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: theme.palette.text.primary }}>
                Generated Files:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 3 }}>
                <Chip
                  label={results?.summaryFile || 'PO DC Summary.xlsx'}
                  size="small"
                  variant="outlined"
                  icon={<Download style={{ fontSize: 15 }} />}
                  clickable={!!results?.processId}
                  onClick={results?.processId ? handleDownloadSummary : undefined}
                  sx={{
                    fontSize: '0.7rem',
                    borderColor: results?.processId ? TOOL_COLORS.PDF.primary : theme.palette.divider,
                    color: results?.processId ? TOOL_COLORS.PDF.primary : theme.palette.text.secondary,
                  }}
                />
                <Chip
                  label={results?.importFile || 'PO Import.xlsx'}
                  size="small"
                  variant="outlined"
                  icon={<Download style={{ fontSize: 15 }} />}
                  clickable={!!results?.processId}
                  onClick={results?.processId ? handleDownloadImport : undefined}
                  sx={{
                    fontSize: '0.7rem',
                    borderColor: results?.processId ? TOOL_COLORS.PDF.primary : theme.palette.divider,
                    color: results?.processId ? TOOL_COLORS.PDF.primary : theme.palette.text.secondary,
                  }}
                />
              </Box>

              {/* Instructions */}
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: theme.palette.text.primary }}>
                Instructions:
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
                1. Upload Order PDF file (PO information)<br />
                2. Upload Distribution/DC PDF file<br />
                3. Click "Process Files" to extract data<br />
                4. Download generated Excel files<br /><br />
                <strong style={{ color: theme.palette.text.primary }}>Output:</strong> Two Excel files containing processed PO and DC information
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default HomeGoods;
